/**
 * @module app.utils
 */
import {LAYER_ORDER, MAP_PROJECTION, MOUNTAINEERING_LAYERS} from './constants.js';
import olStyleIcon from 'ol/style/Icon.js';
import olStyleStyle from 'ol/style/Style.js';
import {proj as proj2056} from '@geoblocks/proj/src/EPSG_2056.js';
import {lerp} from "ol/math";
import {distance as olDistance} from "ol/coordinate";
import {fromLonLat, get, toLonLat, transform, transformExtent} from 'ol/proj';
import {asArray} from 'ol/color';
import {createOrUpdate, buffer as bufferExtent} from 'ol/extent';


/**
 * @const {RegExp}
 */
const NUMBER_WITH_SPACES_REGEXP = /\B(?=(\d{3})+(?!\d))/g;


/**
 * @const {ol.proj.Projection}
 */
const lonLat = get('EPSG:4326');

export const QUERY_HIGHLIGHT_ID = 'queryHighlight';


/**
 * @param {string} key Layer name
 * @return {number}
 */
export function layerIndex(key) {
  for (let i = 0, ii = LAYER_ORDER.length; i < ii; i++) {
    if (key.match(LAYER_ORDER[i])) {
      return i;
    }
  }
  console.assert(false);
}

/**
 * Capitalizes a string, i.e. converts the first letter to uppercase
 * and all other letters to lowercase, e.g.:
 *
 * @param {string} str String value to capitalize.
 * @return {string} String value with first letter in uppercase.
 */
export function capitalize(str) {
  return String(str.charAt(0)).toUpperCase() + String(str.substr(1)).toLowerCase();
}


/**
 * @param {string} name The name to check.
 * @return {boolean} whether name is a winter layer
 */
export function isWinterLayer(name) {
  return [
    'crosscountry', 'winterhiking', 'snowshoe', 'sledging', 'skitour'
  ].includes(name);
}

/**
 * @param {string} name The name to check.
 * @return {boolean} whether name is a winter layer
 */
export function isSingleWinterLayer(name) {
  return [
    'winterhiking', 'snowshoe', 'sledging', 'skitour'
  ].includes(name);
}


/**
 * @const
 * @type {!Object.<string, *>}
 */
export const NO_HTTP_CREDENTIALS = {
  'withCredentials': false
};


/**
 * @param {Object<string, T>} obj
 * @param {function(T, string)} fn
 * @template T
 */
export function objectForEach(obj, fn) {
  for (const key in obj) {
    fn(obj[key], key);
  }
}


/**
 * @param {Object<string, T>} obj
 * @param {function(T, string):boolean} fn
 * @return {Object<string, T>} filtered object
 * @template T
 */
export function objectFilter(obj, fn) {
  const res = {};
  for (const key in obj) {
    if (fn(obj[key], key)) {
      res[key] = obj[key];
    }
  }
  return res;
}


/**
 * @param {Object<string, T>} obj
 * @param {function(T, string):boolean} fn
 * @return {boolean}
 * @template T
 */
export function objectSome(obj, fn) {
  for (const key in obj) {
    if (fn(obj[key], key)) {
      return true;
    }
  }
  return false;
}


/**
 * @return {boolean}
 */
export function smallScreen() {
  return window.matchMedia ? window.matchMedia('(max-width: 767px)').matches : false;
}


/**
 * @param {string} template Template.
 * @param {Object} obj Source.
 * @return {string}
 */
export function fillTemplate(template, obj) {
  objectForEach(obj, function(value, key) {
    template = template.replace('{' + key + '}', value);
  });
  return template;
}


/**
 * @param {number} num
 * @return {string}
 */
export function numberWithSpaces(num) {
  return num.toFixed(0).replace(NUMBER_WITH_SPACES_REGEXP, ' ');
}


/**
 * Returns the greatest common divisor.
 * @param {number} a
 * @param {number} b
 * @return {number} greatest common divisor
 */
export function gcd(a, b) {
  a = Math.abs(a);
  b = Math.abs(b);
  if (b > a) {
    const temp = a;
    a = b;
    b = temp;
  }
  while (true) {
    if (b == 0) {
      return a;
    }
    a %= b;
    if (a == 0) {
      return b;
    }
    b %= a;
  }
}

/**
 * Round coordinates.
 * @param {Array<number>} coordinates The coordinates to round.
 * @param {number} factor The rounding factor, 10^precision.
 * @return {Array<number>}
 */
export function roundCoordinates(coordinates, factor) {
  coordinates[0] = Math.round(factor * coordinates[0]) / factor;
  coordinates[1] = Math.round(factor * coordinates[1]) / factor;
  if (coordinates.length > 2) {
    coordinates[2] = Math.round(factor * coordinates[2]) / factor;
  }
  if (coordinates.length > 3) {
    coordinates[3] = Math.round(factor * coordinates[3]) / factor;
  }
  return coordinates;
}

/**
 * @param {Array.<ol.Extent>} extents Extents.
 * @param {ol.Extent=} opt_extent Destination extent.
 * @return {ol.Extent} Bounding extent.
 */
export function extentMerge(extents, opt_extent) {
  if (extents.length === 0) {
    return null;
  }
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (let i = 0, ii = extents.length; i < ii; i++) {
    const extent = extents[i];
    minX = Math.min(minX, extent[0]);
    minY = Math.min(minY, extent[1]);
    maxX = Math.max(maxX, extent[2]);
    maxY = Math.max(maxY, extent[3]);
  }
  return createOrUpdate(minX, minY, maxX, maxY, opt_extent);
}


/**
 * @type {!Object.<string, ol.style.Style>}
 * @private
 */
export const poiStyleCache_ = {};

/**
 * @param {string} layername Layer name.
 * @param {number} resolution Resolution.
 * @param {app.CacheBustService} cacheBustService
 * @return {ol.style.Style} Style.
 */
export function poiStyle(layername, resolution, cacheBustService) {
  let sizeOriginal = 22;
  if (layername === 'Place') {
    sizeOriginal = 44;
  } else if (MOUNTAINEERING_LAYERS.includes(layername)) {
    sizeOriginal = 31;
  }

  let sizeScaled = 11;
  if (MOUNTAINEERING_LAYERS.includes(layername)) {
    sizeScaled = 30;
    if (resolution > 50) {
      sizeScaled = 15;
      if (resolution >= 250) {
        sizeScaled = 7;
      }
    }
  } else if (resolution >= 250) {
    sizeScaled = 5;
  } else if (resolution <= 50) {
    if (layername === 'Place' && resolution <= 20) {
      sizeScaled = 44;
    } else {
      sizeScaled = 22;
    }
  }

  const key = `${layername}-${sizeScaled}`;

  let style = poiStyleCache_[key];
  if (!style) {
    style = new olStyleStyle({
      image: new olStyleIcon({
        scale: sizeScaled / sizeOriginal,
        size: [sizeOriginal, sizeOriginal],
        src: cacheBustService.addSuffix(`img/poi/${layername}_22.png`)
      })
    });
    poiStyleCache_[key] = style;
  }
  return style;
}


/**
 * see http://stackoverflow.com/a/35326384
 * @param {jQuery} element
 * @param {function()} callback
 */
export function onRendered(element, callback) {
  angular['getTestability'](element)['whenStable'](() => {
    callback();
  });
}


/**
 * The DOM of the currently visible sub menu. null if no sub-menu is displayed.
 * @return {jQuery}
 */
export function visibleSubMenu() {
  const visibleMenuItems = $('.menu-item').filter(':visible');
  return visibleMenuItems.length === 1 ? visibleMenuItems : null;
}

/**
 * Returns an array of CSS rules whose selector matches a regExp.
 * @param {RegExp} regExp
 * @return {!Array<string>}
 */
export function getMatchingStyles(regExp) {
  const matchingStyles = [];
  const {styleSheets} = window.document;
  for (let i = 0; i < styleSheets.length; i++) {
    let cssRules;
    try {
      cssRules = styleSheets[i].cssRules;
    } catch (err) {
      // Ignore SecurityError, see https://developer.mozilla.org/en-US/docs/Web/API/CSSStyleSheet#Notes
      continue;
    }
    for (let j = 0; j < cssRules.length; j++) {
      const {selectorText, style} = cssRules[j];
      if (!selectorText) {
        continue;
      }
      const selectors = selectorText.split(',');
      selectors.forEach((selector) => {
        if (regExp.test(selector)) {
          const rule = `${selector} { ${style.cssText} }`;
          matchingStyles.push(rule);
        }
      });
    }
  }
  return matchingStyles;
}

/**
 * Returns an RGBA array from a given color + opacity. The color
 * can be given as an array or a hex string.
 * @param {ol.Color|string} color
 * @param {number} opacity
 * @return {ol.Color}
 */
export function colorWithOpacity(color, opacity) {
  const rgba = asArray(color);
  rgba[3] = opacity;
  return rgba;
}

/**
 * Add a buffer in pixels, given an extent in meters and a resolution in pixels per meter
 * @param {ol.Extent} extent
 * @param {number} resolution
 * @param {number} buffer
 * @return {ol.Extent}
 */
export function addBuffer(extent, resolution, buffer) {
  const bufferMeters = buffer * resolution;
  return bufferExtent(extent, bufferMeters);
}

/**
 * Returns true if the browser supports <input type="date">
 * Source: https://stackoverflow.com/a/10199306
 */
export function hasNativeDateInput() {
  const input = document.createElement('input');
  input.setAttribute('type', 'date');

  const notADateValue = 'not-a-date';
  input.setAttribute('value', notADateValue);

  return (input.value !== notADateValue);
}

/**
 * @param {ol.Coordinate} coordinates
 * @return {ol.Coordinate}
 */
export function lv95ToMapProjection(coordinates) {
  return transform(coordinates, proj2056, MAP_PROJECTION);
}

/**
 * @param {ol.Extent} extent
 * @return {ol.Extent}
 */
export function lv95ExtentToMapProjection(extent) {
  return transformExtent(extent, proj2056, MAP_PROJECTION);
}

/**
 * @param {ol.Extent} extent
 * @return {ol.Extent}
 */
export function mapProjectionExtentToLonLat(extent) {
  return transformExtent(extent, MAP_PROJECTION, lonLat);
}

/**
 * @param {ol.Coordinate} coordinates
 * @return {ol.Coordinate}
 */
export function mapProjectionToLv95(coordinates) {
  return transform(coordinates, MAP_PROJECTION, proj2056);
}

/**
 * @param {ol.Coordinate} coordinates
 * @return {ol.Coordinate}
 */
export function lonLatToMapProjection(coordinates) {
  return fromLonLat(coordinates, MAP_PROJECTION);
}


/**
 * @param {ol.Coordinate} coordinates
 * @return {ol.Coordinate}
 */
export function mapProjectionToLonLat(coordinates) {
  return toLonLat(coordinates, MAP_PROJECTION);
}


/**
 * @param {GeoJSONObject} geojson
 * @param {function(ol.Coordinate): ol.Coordinate} projectionFunction
 */
export function reprojectInPlace(geojson, projectionFunction) {
  if (geojson.type === 'FeatureCollection') {
    geojson.features.forEach(f => reprojectInPlace(f, projectionFunction));
    return;
  }
  const geometry = geojson.geometry;
  // https://fr.wikipedia.org/wiki/GeoJSON
  if (geometry.type === 'Point') {
    geometry.coordinates = projectionFunction(geojson.coordinates);
  } else if (geometry.type === 'LineString' || geometry.type === 'MultiPoint') {
    geometry.coordinates.forEach((coo, idx, parent) => parent[idx] = projectionFunction(coo));
  } else if (geometry.type === 'MultiLineString' || geometry.type === 'Polygon') {
    geometry.coordinates.forEach(linestringOrRing => {
      linestringOrRing.forEach((coo, idx, parent) => parent[idx] = projectionFunction(coo));
    });
  } else if (geometry.type === 'MultiPolygon') {
    geometry.coordinates.forEach(polygon => {
      polygon.forEach(ring => {
        ring.forEach((coo, idx, parent) => parent[idx] = projectionFunction(coo));
      });
    });
  } else {
    throw new Error('Cannot reprojectInPlace ' + geojson);
  }
}


/**
 * Taken from ol.geom.flat.closest
 * Returns the point on the 2D line segment flatCoordinates[offset1] to
 * flatCoordinates[offset2] that is closest to the point (x, y).  Extra
 * dimensions are linearly interpolated.
 * @param {Array<number>} flatCoordinates Flat coordinates.
 * @param {number} offset1 Offset 1.
 * @param {number} offset2 Offset 2.
 * @param {number} stride Stride.
 * @param {number} x X.
 * @param {number} y Y.
 * @param {Array<number>} closestPoint Closest point.
 */
export function olAssignClosest(flatCoordinates, offset1, offset2, stride, x, y, closestPoint) {
  const x1 = flatCoordinates[offset1];
  const y1 = flatCoordinates[offset1 + 1];
  const dx = flatCoordinates[offset2] - x1;
  const dy = flatCoordinates[offset2 + 1] - y1;
  let offset;
  if (dx === 0 && dy === 0) {
    offset = offset1;
  } else {
    const t = ((x - x1) * dx + (y - y1) * dy) / (dx * dx + dy * dy);
    if (t > 1) {
      offset = offset2;
    } else if (t > 0) {
      for (let i = 0; i < stride; ++i) {
        closestPoint[i] = lerp(flatCoordinates[offset1 + i], flatCoordinates[offset2 + i], t);
      }
      closestPoint.length = stride;
      return;
    } else {
      offset = offset1;
    }
  }
  for (let i = 0; i < stride; ++i) {
    closestPoint[i] = flatCoordinates[offset + i];
  }
  closestPoint.length = stride;
}

/**
 * Taken from old version of ngeo
 * A function that changes Angular's $location provider to avoid problem
 * when both ngeoLocation and $location are used in an application. This
 * is how you can use that function in an application:
 *
 * app.module.config(ngeo.statemanager.Location.MockProvider);
 *
 * @param {angular.$locationProvider} $locationProvider Angular location
 *     provider.
 * @ngInject
 */
export function mockProvider($locationProvider) {
  /**
   * @return {angular.$location} Mock object for Angular location service.
   */
  $locationProvider['$get'] = function() {
    const locationMock = /** @type {angular.$location} */ ({
      /**
       * @return {string} Absolute URL.
       */
      absUrl() {
        return '';
      },
      /**
       * @param {string=} opt_path Path.
       * @return {string} Hash.
       */
      hash(opt_path) {
        return opt_path !== undefined ? this : '';
      },
      /**
       * @return {string} Host.
       */
      host() {
        return '';
      },
      /**
       * @param {string=} opt_path Path.
       * @return {string} Path.
       */
      path(opt_path) {
        return opt_path !== undefined ? this : '';
      },
      /**
       * @return {number} Port.
       */
      port() {
        return 0;
      },
      /**
       * @return {string} Protocol.
       */
      protocol() {
        return '';
      },
      replace() {
      },
      /**
       * @param {string=} opt_search Search.
       * @param {Object=} opt_paramValue Parameters.
       * @return {Object} Search.
       */
      search(opt_search, opt_paramValue) {
        return opt_search !== undefined ? this : {};
      },
      /**
       * @param {string=} opt_url URL.
       * @return {string} URL.
       */
      url(opt_url) {
        return '';
      }
    });
    return locationMock;
  };
}

/**
* @export
* @param {*} val
* @return {boolean}
*/
export function isNumber(val) {
  return typeof val === 'number';
}

/**
 * @export
 * @param {string} link
 * @return {string}
 */
export function isExternalLink(link) {
  const regexp = new RegExp('(schweizmobil|slowup|schweizmobilplus|' +
      'suissemobileplus|(velo|wander|skating|mountainbike|kanu)land).ch');
  return regexp.test(link) ? 'internal' : 'external';
}

export function getConstrainedResolution(view, resolution, step = 0) {
  view.setConstrainResolution(true); // enable constrain to getConstrainedResolution (dont work with disabled constrain)
  const constrainedResolution = view.getConstrainedResolution(resolution, step); // compute the next resolution
  view.setConstrainResolution(false);
  return constrainedResolution;
}

export function includeBbox(biggerBox, smallBox) {
  const lowerPoints = biggerBox.filter((b, key) => b <= smallBox[key]);
  const highestPoints = biggerBox.filter((b, key) => b >= smallBox[key]);
  return lowerPoints.length === 2 && highestPoints.length === 2;
}

/**
 * Returns true if map extent includes coordinates
 * @param {Array<number>} coordinates
 * @returns {boolean|boolean}
 */
export function isMapExtentInclude(coordinates) {
  const extent = MAP_PROJECTION.getExtent();
  const xIncluded = coordinates[0] > extent[0] && coordinates[0] < extent[2];
  const yIncluded = coordinates[1] > extent[1] && coordinates[1] < extent[3];
  return xIncluded && yIncluded;
}

/**
 * Replaces special characters in string
 * @param string
 * @return {string|*}
 */
export function replaceSpecialCharacters(string) {
  if (!string) {
    return '';
  }
  const conversions = {};
  conversions['ae'] = 'ä|æ|ǽ';
  conversions['oe'] = 'ö|œ';
  conversions['ue'] = 'ü';
  conversions['Ae'] = 'Ä';
  conversions['Ue'] = 'Ü';
  conversions['Oe'] = 'Ö';
  conversions['A'] = 'À|Á|Â|Ã|Ä|Å|Ǻ|Ā|Ă|Ą|Ǎ';
  conversions['a'] = 'à|á|â|ã|å|ǻ|ā|ă|ą|ǎ|ª';
  conversions['C'] = 'Ç|Ć|Ĉ|Ċ|Č';
  conversions['c'] = 'ç|ć|ĉ|ċ|č';
  conversions['D'] = 'Ð|Ď|Đ';
  conversions['d'] = 'ð|ď|đ';
  conversions['E'] = 'È|É|Ê|Ë|Ē|Ĕ|Ė|Ę|Ě';
  conversions['e'] = 'è|é|ê|ë|ē|ĕ|ė|ę|ě';
  conversions['G'] = 'Ĝ|Ğ|Ġ|Ģ';
  conversions['g'] = 'ĝ|ğ|ġ|ģ';
  conversions['H'] = 'Ĥ|Ħ';
  conversions['h'] = 'ĥ|ħ';
  conversions['I'] = 'Ì|Í|Î|Ï|Ĩ|Ī|Ĭ|Ǐ|Į|İ';
  conversions['i'] = 'ì|í|î|ï|ĩ|ī|ĭ|ǐ|į|ı';
  conversions['J'] = 'Ĵ';
  conversions['j'] = 'ĵ';
  conversions['K'] = 'Ķ';
  conversions['k'] = 'ķ';
  conversions['L'] = 'Ĺ|Ļ|Ľ|Ŀ|Ł';
  conversions['l'] = 'ĺ|ļ|ľ|ŀ|ł';
  conversions['N'] = 'Ñ|Ń|Ņ|Ň';
  conversions['n'] = 'ñ|ń|ņ|ň|ŉ';
  conversions['O'] = 'Ò|Ó|Ô|Õ|Ō|Ŏ|Ǒ|Ő|Ơ|Ø|Ǿ';
  conversions['o'] = 'ò|ó|ô|õ|ō|ŏ|ǒ|ő|ơ|ø|ǿ|º';
  conversions['R'] = 'Ŕ|Ŗ|Ř';
  conversions['r'] = 'ŕ|ŗ|ř';
  conversions['S'] = 'Ś|Ŝ|Ş|Š';
  conversions['s'] = 'ś|ŝ|ş|š|ſ';
  conversions['T'] = 'Ţ|Ť|Ŧ';
  conversions['t'] = 'ţ|ť|ŧ';
  conversions['U'] = 'Ù|Ú|Û|Ũ|Ū|Ŭ|Ů|Ű|Ų|Ư|Ǔ|Ǖ|Ǘ|Ǚ|Ǜ';
  conversions['u'] = 'ù|ú|û|ũ|ū|ŭ|ů|ű|ų|ư|ǔ|ǖ|ǘ|ǚ|ǜ';
  conversions['Y'] = 'Ý|Ÿ|Ŷ';
  conversions['y'] = 'ý|ÿ|ŷ';
  conversions['W'] = 'Ŵ';
  conversions['w'] = 'ŵ';
  conversions['Z'] = 'Ź|Ż|Ž';
  conversions['z'] = 'ź|ż|ž';
  conversions['AE'] = 'Æ|Ǽ';
  conversions['ss'] = 'ß';
  conversions['IJ'] = 'Ĳ';
  conversions['ij'] = 'ĳ';
  conversions['OE'] = 'Œ';
  conversions['f'] = 'ƒ';

  for (const i in conversions) {
    const re = new RegExp(conversions[i], "g");
    string = string.replace(re, i);
  }

  return string;
}

/**
 * Calculates via_points from coordinates on equal distance (as much as it is possible).
 * minPointsCount takes into account if coordinates length more that minPointsCount. First and last points always returns.
 * @param {[number[]]} coordinates
 * @param {number} length - length in meters
 * @param minPointsCount
 * @return {[number[]]}
 */
export function calculateViaPoints(coordinates, length, minPointsCount = 3) {
  let viaPoints;
  if (!length) {
    length = 0;
    coordinates.forEach((coord, idx) => {
      if (idx !== 0) {
        const prevIdx = idx - 1;
        length += olDistance(coord, coordinates[prevIdx]);
      }
    });
  }
  const lengthKm = Math.round(length / 1000);
  const pointsCount = lengthKm <= minPointsCount ? minPointsCount : lengthKm;
  if (coordinates.length <= pointsCount) {
    viaPoints = coordinates.slice();
  } else {
    const minPointsDistance = length / pointsCount - 50; // 50 is offset for better precision
    let distance = 0;
    coordinates = coordinates.filter((coord, idx) => {
      const prevIdx = idx - 1;
      if (idx === 0 || idx === coordinates.length - 1) {
        return true;
      }
      distance += olDistance(coord, coordinates[prevIdx]);
      if (distance >= minPointsDistance) {
        distance = 0;
        return true;
      }
      return false;
    });
    viaPoints = coordinates.slice();
  }
  return viaPoints;
}

/**
 * Calculates shifted position for point along line
 * @param lineString
 * @param pointRadius
 * @param resolution
 * @param isPrint
 * @return {*}
 */
export function shiftPositionAlongLine(lineString, pointRadius, resolution, isPrint = false) {
  const diameter = pointRadius * 2;
  let lineLength = lineString.getLength();
  lineLength = lineLength > 1000 ? lineLength : 1000;
  lineLength /= resolution;
  let fractionDistanceToB = diameter / lineLength;
  if (isPrint) {
    fractionDistanceToB *= 3;
  }
  return lineString.getCoordinateAt(1 - fractionDistanceToB);
}
